import { useEffect, useState } from "react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { useNavigate } from "react-router-dom";
import { useAuth, isSuperadmin, isAdmin } from "@/lib/auth";
import api from "@/lib/api";
import { LayoutDashboard, KanbanSquare, ListTree, Users, Building2, Ticket, Settings, Sun, FileBarChart, Layers } from "lucide-react";

export default function CommandPalette({ open, setOpen, role = "admin" }) {
  const nav = useNavigate();
  const { toggleTheme, user } = useAuth();
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (!open) return;
    api.tickets({ limit: 8 }).then((r) => setTickets(r?.tickets || [])).catch(() => {});
  }, [open]);

  const go = (path) => { setOpen(false); nav(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search tickets, navigate…" data-testid="command-search" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>
        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/portal")}><LayoutDashboard className="mr-2 h-4 w-4" />Staff Portal</CommandItem>
          {isAdmin(user) && (
            <>
              <CommandItem onSelect={() => go("/admin")}><LayoutDashboard className="mr-2 h-4 w-4" />Admin Overview</CommandItem>
              <CommandItem onSelect={() => go("/admin/kanban")}><KanbanSquare className="mr-2 h-4 w-4" />Kanban Board</CommandItem>
              <CommandItem onSelect={() => go("/admin/queue")}><ListTree className="mr-2 h-4 w-4" />Ticket Queue</CommandItem>
              <CommandItem onSelect={() => go("/admin/reports")}><FileBarChart className="mr-2 h-4 w-4" />Reports</CommandItem>
              <CommandItem onSelect={() => go("/admin/categories")}><Layers className="mr-2 h-4 w-4" />Categories &amp; Services</CommandItem>
              <CommandItem onSelect={() => go("/admin/team")}><Users className="mr-2 h-4 w-4" />Users &amp; Team</CommandItem>
            </>
          )}
          {isSuperadmin(user) && (
            <CommandItem onSelect={() => go("/superadmin")}><Building2 className="mr-2 h-4 w-4" />Superadmin</CommandItem>
          )}
        </CommandGroup>
        <CommandSeparator />
        {tickets.length > 0 && (
          <CommandGroup heading="Recent tickets">
            {tickets.map((t) => (
              <CommandItem
                key={t.id}
                onSelect={() => go(role === "portal" ? "/portal/tickets" : "/admin/queue")}
              >
                <Ticket className="mr-2 h-4 w-4" />
                <span className="text-mono text-xs mr-2">{t.ticket_no}</span>
                <span className="truncate">{t.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem onSelect={() => { toggleTheme(); setOpen(false); }}>
            <Sun className="mr-2 h-4 w-4" />Toggle theme
          </CommandItem>
          <CommandItem onSelect={() => go(isAdmin(user) ? "/admin/settings" : "/portal")}>
            <Settings className="mr-2 h-4 w-4" />Settings
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
